import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { IaFilesService } from 'src/app/services/ia-files.service';
import { UserService } from 'src/app/services/user.service';
import { AngularFirestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/storage';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { ClasesService } from 'src/app/services/clases.service';

// Se asume que MathJax se carga en el index.html
declare global {
  interface Window {
    MathJax: any;
  }
}

@Component({
  selector: 'app-iachat',
  templateUrl: './iachat.component.html',
  styleUrls: ['./iachat.component.css'],
})
export class IachatComponent implements OnInit, OnDestroy {
  mainuserid!: string;
  mainuseremail: any;
  usuario: any;
  claseuid: string = '';

  // Gestión de sesiones de chat
  chatSessions: any[] = [];
  currentChatSessionId: string = '';
  groupedChatSessions: { [claseId: string]: any[] } = {};

  // Historial de conversación (acumulativo)
  conversationHistory: { role: string; content: string }[] = [
    {
      role: 'system',
      content:
        'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
    },
  ];

  // Variables para el chat
  chatmessage: string = '';
  chatIa: any[] = []; // Mensajes obtenidos de Firestore
  loadingResponse: boolean = false;
  typingMessage: string = '';
  formattedTypingMessage: SafeHtml = '';

  // Documentos
  documentos: any[] = [];
  porcentaje: number | null = null;

  // Suscripciones
  private documentosSubscription!: Subscription;
  private chatMessagesSubscription!: Subscription;
  private chatSessionsSubscription!: Subscription;

  // Estado de la interfaz
  mostrarChat: boolean = false;

  // Variables opcionales para actualizar título, etc.
  private iteracionesChat: number = 0;
  private iteracionesParaActualizarTitulo: number = 6;

  clases: any[] = []; // Lista de clases

  selectedClaseId: string = ''; // Clase seleccionada para filtrar chats
  clasesUsuario: any[] = []; // Clases del usuario

  constructor(
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private iaFilesService: IaFilesService,
    private translate: TranslateService,
    private firestore: AngularFirestore,
    private angularfireStorage: AngularFireStorage,
    private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private clasesService: ClasesService
  ) {
    // Configura marked para que preserve saltos de línea en Markdown (útil para fórmulas multilinea)
    marked.setOptions({
      breaks: true,
    });
  }
  // --- Variables para bienvenida, sugerencias y recordatorio ---
  welcomeMessage: string = '👋 ¡Hola! Soy tu asistente académico. ¡Empecemos!';
  showWelcomePanel: boolean = false;
  quickSuggestions: string[] = [
    '¿Puedes sugerirme una secuencia didáctica basada en los materiales subidos?',
    'Genera una rúbrica de evaluación para esta asignatura.',
    'Sugiere adaptaciones del contenido para estudiantes con necesidades educativas especiales.',
    'Crea un examen tipo test con 10 preguntas sobre la asignatura.',
    '¿Qué actividades prácticas puedo incluir para reforzar el aprendizaje?',
    '¿Cómo puedo explicar este tema con un enfoque más visual o interactivo?',
    '¿Qué errores comunes cometen los estudiantes al abordar la asignatura y cómo los puedo prevenir?',
    'Ayúdame a convertir este contenido en una presentación clara para clase.',
    'Crea una lista de ejercicios con distintos niveles de dificultad sobre esta asignatura.',
    'Elabora preguntas tipo examen sobre la asignatura, con sus respuestas.	',
    '¿Qué actividad colaborativa puedo aplicar con este contenido?',
    'Diseña una presentación o esquema visual de la asignatura para proyectar en clase.',
  ];
  mostrarSugerenciaSubirDocs: boolean = false;
  interaccionesSinDocs: number = 0;

  asistentePerfil = {
    nombre: 'Asistente Académico',
    descripcion:
      'Especialista en diseño de cursos, exámenes y material educativo.',
  };
  // --- Fin de variables opcionales ---
  ngOnInit(): void {
    // Obtener parámetro de clase desde la URL y cargar documentos
    this.activatedRoute.params.subscribe((params) => {
      this.claseuid = params['claseuid'];
      this.loadDocuments(); // Cargar documentos asociados a la clase
    });

    // Obtener autenticación y datos del usuario
    this.authenticationService.getStatus().subscribe(
      (status) => {
        this.mainuserid = status.uid;
        this.mainuseremail = status.email;

        this.userService.getUserById(this.mainuserid).subscribe((user) => {
          if (user && user.length > 0) {
            this.usuario = user[0].payload.doc.data();
            this.cargarClasesUsuario(this.usuario.clases || []);

            // Cambiar sugerencias y comportamiento según el rol del usuario
            if (this.usuario.rol === 'estudiante') {
              this.quickSuggestions = [
                '¿Qué preguntas me podrían hacer en un examen sobre este tema?',
                '¿En qué debo enfocarme para repasar este contenido?',
                'Dame un esquema de estudio para prepararme sobre lo que hemos visto',
                '¿Me puedes decir de donde puedo sacar material de apoyo para la asignatura?',
                '¿Me puedes explicar este tema como si no supiese nada?',
                '¿Qué conceptos clave debo dominar en esta asignatura?',
                'Dame ideas para desarrollar una presentación basada en lo visto en clase.',
                'Hazme una lista de definiciones importantes de la asignatura.',
                'Proponme ejercicios con diferentes niveles de dificultad.',
              ];

              this.asistentePerfil = {
                nombre: 'Asistente de Estudio',
                descripcion: 'Te ayudo a entender mejor tus asignaturas.',
              };

              this.conversationHistory = [
                {
                  role: 'system',
                  content:
                    'Eres un asistente académico que apoya a estudiantes a comprender mejor la asignatura que están cursando. Usa el estilo del profesor y adapta tus respuestas al nivel del alumno.',
                },
              ];
            }
          }

          this.loadChatSessions(); // Cargar historial de chats
        });
      },
      (error) => console.error('Error al obtener autenticación:', error)
    );

    // Suscribirse a los documentos relacionados con la clase
    this.documentosSubscription = this.firestore
      .collection('documentos', (ref) =>
        ref.where('clase', '==', this.claseuid)
      )
      .valueChanges({ idField: 'id' })
      .subscribe(
        (docs: any[]) => {
          this.documentos = docs;
        },
        (error) => console.error('Error al cargar documentos:', error)
      );
  }

  ngOnDestroy(): void {
    if (this.documentosSubscription) this.documentosSubscription.unsubscribe();
    if (this.chatMessagesSubscription)
      this.chatMessagesSubscription.unsubscribe();
    if (this.chatSessionsSubscription)
      this.chatSessionsSubscription.unsubscribe();
  }

  cargarClasesUsuario(clases): void {
    clases.forEach((claseId) => {
      this.clasesService
        .getClaseByIdNow(claseId)
        .subscribe((claseDatos: any) => {
          if (claseDatos.exists) {
            this.clasesUsuario.push({
              id: claseId,
              datos: claseDatos.data(),
            });
          }
        });
    });
  }

  formatearFecha(fecha: Date): string {
    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const año = fecha.getFullYear();
    return `${dia}/${mes}/${año}`;
  }

  // Convierte Markdown a HTML seguro
  parseMarkdown(content: string | undefined | null): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(marked(content || ''));
  }

  // -------------------------------------------------------------------
  // Métodos de Sesiones de Chat
  // -------------------------------------------------------------------

  loadChatSessions(): void {
    this.chatSessionsSubscription = this.firestore
      .collection('chatsFilesIa', (ref) =>
        ref.where('userId', '==', this.mainuserid).orderBy('createdAt', 'desc')
      )
      .snapshotChanges()
      .subscribe(
        (sessions: any[]) => {
          this.chatSessions = sessions.map((s) => ({
            id: s.payload.doc.id,
            ...s.payload.doc.data(),
          }));
          this.groupChatSessions();
          if (!this.currentChatSessionId && this.chatSessions.length > 0) {
            this.openChatSession(this.chatSessions[0]);
          }
        },
        (error) => console.error('Error al cargar sesiones de chat:', error)
      );
  }

  groupChatSessions(): void {
    const grupos: { [claseId: string]: any[] } = {};

    this.chatSessions.forEach((session) => {
      const claseId = session.clase || 'Sin clase';
      if (!grupos[claseId]) {
        grupos[claseId] = [];
      }
      grupos[claseId].push(session);
    });

    this.groupedChatSessions = grupos;

    // Actualiza clases sin acumulación ni duplicados
    this.cargarClases();
  }

  cargarClases(): void {
    const claseIdsUnicos = Array.from(
      new Set(this.chatSessions.map((s) => s.clase || 'Sin clase'))
    );

    this.clases = []; // limpiamos para que no se acumulen

    claseIdsUnicos.forEach((claseId) => {
      this.clasesService
        .getClaseByIdNow(claseId)
        .subscribe((claseDatos: any) => {
          if (claseDatos.exists) {
            this.clases.push({
              id: claseId,
              datos: claseDatos.data(),
            });
          }
        });
    });
  }

  // Devuelve la lista de clases ordenadas
  groupedClaseKeys(): string[] {
    return Object.keys(this.groupedChatSessions);
  }

  // Devuelve un nombre más bonito de la clase, si quieres
  getNombreClase(claseId: string): string {
    // Aquí podrías buscar el nombre en una lista de clases.
    if (claseId === 'Sin clase') return 'Chats sin clase';
    // O si tienes una lista de clases cargadas, buscar el nombre real:
    // return this.listaClases.find(c => c.id === claseId)?.nombre || claseId;
    return `Clase ${claseId}`;
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  isYesterday(date: Date): boolean {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear()
    );
  }

  isWithinLast7Days(date: Date): boolean {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return date >= sevenDaysAgo;
  }

  newChat(): void {
    const defaultTitle = 'Nuevo Chat ' + new Date().toLocaleDateString();
    const newSession = {
      userId: this.mainuserid,
      title: defaultTitle,
      createdAt: new Date(),
      clase: this.claseuid, // Se añade para filtrar por clase en el historial
    };
    this.firestore
      .collection('chatsFilesIa')
      .add(newSession)
      .then((docRef) => {
        newSession['id'] = docRef.id;
        this.chatSessions.unshift(newSession);
        this.groupChatSessions();
        this.currentChatSessionId = docRef.id;
        // Reinicia el historial para el nuevo chat
        this.conversationHistory = [
          {
            role: 'system',
            content:
              'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
          },
        ];
        this.chatIa = [];
        this.subscribeToChatMessages();
      })
      .catch((error) => console.error('Error al crear nuevo chat:', error));
    this.showWelcomePanel = true;
    this.mostrarSugerenciaSubirDocs = false;
    this.interaccionesSinDocs = 0;
  }

  openChatSession(session: any): void {
    // Si se hace clic en una sesión diferente, se asigna la sesión actual
    if (this.currentChatSessionId !== session.id) {
      this.currentChatSessionId = session.id;
      // Aquí se puede optar por cargar el historial almacenado; en este ejemplo se inicia con un mensaje sistema
      if (this.conversationHistory.length === 0) {
        this.conversationHistory.push({
          role: 'system',
          content:
            'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
        });
      }
    }
    this.subscribeToChatMessages();
    if (this.chatIa.length == 0) {
      this.showWelcomePanel = true;
    } else if (this.chatIa.length > 0) {
      this.showWelcomePanel = false;
    }
    this.mostrarSugerenciaSubirDocs = false;
    this.interaccionesSinDocs = 0;
  }

  subscribeToChatMessages(): void {
    if (this.chatMessagesSubscription)
      this.chatMessagesSubscription.unsubscribe();
    if (!this.currentChatSessionId) return;

    this.chatMessagesSubscription = this.iaFilesService
      .getMessages(this.currentChatSessionId)
      .subscribe(
        (messages: any[]) => {
          // Mostrar en la UI
          this.chatIa = messages.map((msg) => {
            const data = msg.payload.doc.data();
            let rawText = data.message || '';
            let html = marked(rawText);
            html = this.fixMatrixNewlines(html);
            html = this.fixExponents(html);
            html = html.replace(
              /\[([\s\S]*?)\]/g,
              (_, formula) => `\\(${formula}\\)`
            );
            data.processedMessage = html;
            return { ...msg, processedMessage: data.processedMessage };
          });

          // (👀 NUEVO) Reconstruir el conversationHistory REAL para IA
          this.conversationHistory = [
            {
              role: 'system',
              content:
                'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
            },
          ];
          messages.forEach((msg) => {
            const data = msg.payload.doc.data();
            if (data.sender === 'openai') {
              this.conversationHistory.push({
                role: 'assistant',
                content: data.message,
              });
            } else {
              this.conversationHistory.push({
                role: 'user',
                content: data.message,
              });
            }
          });

          setTimeout(() => this.renderMathJax(), 0);
        },
        (error) => console.error('Error al cargar mensajes:', error)
      );
  }

  // -------------------------------------------------------------------
  // Métodos de Mensajes del Chat
  // -------------------------------------------------------------------

  sendMessage(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    if (this.chatmessage.trim() !== '' && this.currentChatSessionId) {
      if (this.showWelcomePanel) {
        this.showWelcomePanel = false;
      }
      if (this.documentos.length === 0) {
        this.interaccionesSinDocs++;
        if (this.interaccionesSinDocs >= 3) {
          this.mostrarSugerenciaSubirDocs = true;
        }
      }
      const fecha = new Date();
      const mensajeUsuario = this.chatmessage;

      // Primero agregas el mensaje nuevo al historial
      this.conversationHistory.push({ role: 'user', content: mensajeUsuario });

      // Prepara el contexto de documentos como mensaje system
      const contextoDocumentos = this.construirContextoDocumentos();
      const messagesToSend = [
        { role: 'system', content: contextoDocumentos },
        ...this.conversationHistory,
      ];

      // Enviar mensaje del usuario a Firestore
      const message = {
        sender: this.mainuserid,
        message: mensajeUsuario,
        fecha: fecha,
        nuevo: true,
      };

      this.iaFilesService
        .sendmessage(this.currentChatSessionId, message)
        .then(() => {
          this.loadingResponse = true;
          this.typingMessage = '';

          // Ahora mandamos todo el historial + contexto
          this.iaFilesService.getResponse(messagesToSend).subscribe(
            (response) => {
              const respuesta = response.choices[0].message.content;
              this.typeTextEffect(respuesta, 0);
            },
            (error) => {
              console.error('Error en la respuesta de OpenAI:', error);
              this.loadingResponse = false;
            }
          );
        })
        .catch((error) => console.error('Error al enviar mensaje:', error));

      this.chatmessage = '';
      setTimeout(() => {
        this.isLoading = false;
      }, 10000);
    }
  }

  /**
   * Efecto "typewriter" para mostrar gradualmente la respuesta del asistente.
   * Al finalizar, corrige el contenido (matrices, exponentes) y agrega la respuesta al historial,
   * además de enviarla a Firestore.
   */
  isLoading: boolean = false;
  typeTextEffect(fullText: string, currentIndex: number): void {
    this.isLoading = true;
    if (currentIndex < fullText.length) {
      this.typingMessage += fullText[currentIndex];
      this.formattedTypingMessage = this.parseMarkdown(this.typingMessage);
      setTimeout(() => this.typeTextEffect(fullText, currentIndex + 1), 2);
    } else {
      // Una vez completado el efecto, se procesa el contenido para corregir fórmulas
      this.formattedTypingMessage = this.parseMarkdown(this.typingMessage);
      setTimeout(() => {
        this.processMathFormulas();
        // Agrega la respuesta del asistente al historial
        this.conversationHistory.push({
          role: 'assistant',
          content: this.typingMessage,
        });
        const messageRespuesta = {
          sender: 'openai',
          message: this.typingMessage,
          fecha: new Date(),
          nuevo: true,
        };
        // Envía el mensaje del asistente a Firestore
        this.iaFilesService
          .sendmessage(this.currentChatSessionId, messageRespuesta)
          .catch((error) =>
            console.error('Error al enviar respuesta de OpenAI:', error)
          );
        this.typingMessage = '';
        this.loadingResponse = false;

        // Incrementar contador de iteraciones (ej. cada vez que se finaliza un intercambio)
        this.iteracionesChat++;

        // Cuando se alcancen 4 iteraciones, genera un título usando OpenAI
      }, 0);
      this.isLoading = false;
      if (this.iteracionesChat >= 4) {
        this.generateChatTitleFromInitialMessages();
        this.iteracionesChat = 0;
      }
    }
  }
  generateChatTitleFromInitialMessages(): void {
    if (this.conversationHistory.length < 5) {
      console.warn('No hay suficientes mensajes para generar un título aún.');
      return;
    }

    // Solo tomamos las 4 primeras interacciones reales (ignoramos el primer system)
    const primerosMensajes = this.conversationHistory
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .slice(0, 4)
      .map((msg) => {
        const autor = msg.role === 'user' ? 'Usuario' : 'Asistente';
        return `${autor}: ${msg.content}`;
      })
      .join('\n');

    const promptTitulo = `Basándote en las siguientes interacciones, genera un título breve de 3 a 4 palabras que resuma el tema principal:\n\n${primerosMensajes}`;

    // Llamamos a OpenAI
    this.iaFilesService
      .getResponse([
        {
          role: 'system',
          content:
            'Eres un generador de títulos breves para conversaciones educativas.',
        },
        { role: 'user', content: promptTitulo },
      ])
      .subscribe(
        (response) => {
          const tituloGenerado = response.choices[0].message.content.trim();
          console.log('Título generado automáticamente:', tituloGenerado);

          if (tituloGenerado && this.currentChatSessionId) {
            this.updateTitle(this.currentChatSessionId, tituloGenerado);
          }
        },
        (error) => {
          console.error(
            'Error al generar el título basado en las primeras interacciones:',
            error
          );
        }
      );
  }

  // Corrige saltos de línea en matrices: fuerza el uso de "\\" en bloques de matriz.
  fixMatrixNewlines(text: string): string {
    return text.replace(
      /(\\begin\{pmatrix\})([\s\S]*?)(\\end\{pmatrix\})/g,
      (match, begin, inner, end) => {
        const fixedInner = inner.replace(/\s*\\\s*/g, ' \\\\ ');
        return begin + fixedInner + end;
      }
    );
  }

  updateTitle(chatId: string, newTitle: string): Promise<void> {
    return this.firestore
      .collection('chatsFilesIa')
      .doc(chatId)
      .update({ title: newTitle })
      .then(() => {
        // Actualiza la sesión en la variable local
        const index = this.chatSessions.findIndex((chat) => chat.id === chatId);
        if (index !== -1) {
          this.chatSessions[index].title = newTitle;
          // Forzar cambio de referencia para que Angular detecte el cambio
          this.chatSessions = [...this.chatSessions];
          this.groupChatSessions();
        }
        console.log('Título actualizado correctamente a:', newTitle);
      })
      .catch((error) => {
        console.error('Error al actualizar el título:', error);
      });
  }

  // Corrige exponentes: convierte "n^3" en "n^{3}".
  fixExponents(text: string): string {
    return text.replace(/([a-zA-Z0-9])\^([^{}\s])/g, (match, base, exp) => {
      return `${base}^{${exp}}`;
    });
  }

  // Procesa el contenido matemático del contenedor "chat-container"
  processMathFormulas(): void {
    const chatContainer = document.getElementById('chat-container');
    if (chatContainer) {
      let html = chatContainer.innerHTML;
      html = this.fixMatrixNewlines(html);
      html = this.fixExponents(html);
      html = html.replace(
        /\[([\s\S]*?)\]/g,
        (_, formula) => `\\(${formula}\\)`
      );
      chatContainer.innerHTML = html;
    }
    this.renderMathJax();
  }
  deleteChat(chatId: string): void {
    if (confirm('¿Estás seguro de que deseas borrar este chat?')) {
      // Elimina la sesión de chat de Firestore
      this.firestore
        .collection('chatsFilesIa')
        .doc(chatId)
        .delete()
        .then(() => {
          console.log('Chat eliminado correctamente');
          // Actualiza el arreglo de chatSessions eliminando el chat borrado
          this.chatSessions = this.chatSessions.filter(
            (chat) => chat.id !== chatId
          );
          // Re-agrupar las sesiones para actualizar la vista
          this.groupChatSessions();
          // Si el chat borrado es el actual, limpiar el estado del chat
          if (this.currentChatSessionId === chatId) {
            this.currentChatSessionId = '';
            // Reiniciar el historial y mensajes (ajustar según tu lógica de negocio)
            this.conversationHistory = [
              {
                role: 'system',
                content:
                  'Eres un asesor academico, que busca ayudar y aconsejar a profesores a preparar sus asignaturas.',
              },
            ];
            this.chatIa = [];
          }
        })
        .catch((error) => console.error('Error al borrar el chat:', error));
    }
  }

  renderMathJax(): void {
    if (window.MathJax && window.MathJax.typesetPromise) {
      window.MathJax.typesetPromise()
        .then(() => console.log('MathJax renderizado'))
        .catch((err: any) =>
          console.error('Error al renderizar MathJax:', err)
        );
    } else {
      console.warn('MathJax no está cargado aún.');
    }
  }

  construirPrompt(mensajeUsuario: string): string {
    const chatContexto = this.conversationHistory
      .map((msg) => {
        if (msg.role === 'user') return `Usuario: ${msg.content}`;
        if (msg.role === 'assistant') return `Asistente: ${msg.content}`;
        return `Sistema: ${msg.content}`;
      })
      .join('\n');
    const contextoDocumentos = this.construirContextoDocumentos();
    return `${contextoDocumentos}\n\n${chatContexto}`;
  }

  construirContextoDocumentos(): string {
    let contexto = 'Información de documentos:\n';
    this.documentos.forEach((doc) => {
      contexto += `Documento: ${doc.nombre}\nResumen/Contenido: ${
        doc.contenido || 'Sin contenido disponible'
      }\n\n`;
    });
    return contexto;
  }

  // -------------------------------------------------------------------
  // Métodos para Documentos y Utilidades
  // -------------------------------------------------------------------

  loadDocuments(): void {
    // Los documentos se cargan en ngOnInit mediante la suscripción.
  }

  scrolltobottom(): void {
    const objDiv = document.getElementById('themessagesbox');
    if (objDiv) {
      objDiv.scrollTo({ top: objDiv.scrollHeight, behavior: 'smooth' });
    }
  }
}
